import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { JeevesProductTableComponent, ProductTableRow } from 'gung-standard-jeeves';
import {
  PriceService,
  GungFlowService,
  AuthService,
  LocationConfigService,
  ProductService,
  PriceConfigService,
  Product
} from 'gung-standard';
import { forkJoin, of } from 'rxjs';
import { first } from 'rxjs';
import { CarLinerModalService } from '../../services/car-liner-modal.service';
import { CarLinerPriceService } from '../../services/car-liner-price.service';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'car-liner-product-table',
    templateUrl: './car-liner-product-table.component.html',
    styleUrls: ['./car-liner-product-table.component.scss'],
    standalone: false
})
export class CarLinerProductTableComponent extends JeevesProductTableComponent implements OnInit {
  public isJosam: boolean = environment.projectName === 'josam';
  public shouldDisplayGrossPrice = false;
  public hidePrice: boolean;
  constructor(
    protected priceService: PriceService,
    protected gungFlowService: GungFlowService,
    protected authService: AuthService,
    protected router: Router,
    protected locationConfigService: LocationConfigService,
    protected productService: ProductService,
    protected carLinerModalService: CarLinerModalService
  ) {
    super(priceService, gungFlowService, authService, router, locationConfigService);
  } 

  protected carLinerPriceService = inject(CarLinerPriceService);
  private priceConfigService = inject(PriceConfigService);

  ngOnInit(): void {
    super.ngOnInit();
    this.evaluateShouldDisplayGrossPrice();
    this.priceConfigService.hidePrice.subscribe(hidePrice => (this.hidePrice = hidePrice));
  }

  openIncludedItemsModal(product: Product) {
    this.carLinerModalService.openIncludedItemsModal(product);
  }

  protected subscribeToProducts() {
    if (!this.oldFlow || !this.currentFlow || this.oldFlow.id !== this.currentFlow.id) {
      // remove cached data;
      this.keyedMapData = {};
      this.oldFlow = this.currentFlow;
    }
    const newIds = this.data.map(p => p.id).filter(id => !Object.keys(this.keyedMapData).includes(id));
    if (newIds.length === 0) {
      this.readMappedDataFromCache();
      return;
    }

    const subscription = forkJoin([
      of(this.data),
      this.priceService.getCurrentCustomerPrices(newIds).pipe(first()),
      this.gungFlowService.getSelectedFlow().pipe(first()),
    ])
      .pipe(first())
      .subscribe(data => {
        const prices = data[1];
        let avs = [];
        this.includeAvailability = data[2].useAvailabilities || data[2].requireAvailability;
        if (this.includeAvailability) {
          avs = newIds.map(id => this.getProductAvailability(this.data.find(product => product.id === id)));
        }

        this.keyedMapData = {
          ...this.keyedMapData,
          ...newIds.reduce((acc, curr) => {
            const product = data[0].find(p => p.id === curr);

            if (!product) {
              this.findError = true;
              this.errorMessage += 'No product found. ';
              throw new Error('No product found');
            }

            const price = prices.filter(p => p && p.productId === curr)[0];
            if (!price) {
              this.findError = true;
              this.errorMessage += 'No price found.';
              throw new Error('No price found');
            }

            const av = avs.filter(p => p && p.productId === curr)[0];
            if (!av && this.includeAvailability) {
              this.findError = true;
              this.errorMessage += 'No availability found. ';
              throw new Error('No availability found');
            }

            const item: ProductTableRow = this.mapItem(curr, product, price, av);
            return {
              ...acc,
              [curr]: item
            };
          }, {})
        };
        this.readMappedDataFromCache();
      });
    this.subscriptions.push(subscription);
  }

  evaluateShouldDisplayGrossPrice() {
    const subscription = this.carLinerPriceService.priceMarkupIsEnabled().subscribe(enabled => {
      this.shouldDisplayGrossPrice = enabled;
    });
    this.subscriptions.push(subscription);
  }
}
